const FOAM_CALC_UNIT_TO_CM = { cm: 1, in: 2.54, mm: 0.1 };

/**
 * Minimum order value for cut-to-size, in pence. See the matching note in
 * assets/foam-calculator.js -- both calculators and the foam-quantity-guard
 * validation function must agree on this.
 */
const FOAM_CALC_MIN_ORDER_PENCE = 1000;

if (!customElements.get('foam-calculator-standalone')) {
  customElements.define(
    'foam-calculator-standalone',
    class FoamCalculatorStandalone extends HTMLElement {
      constructor() {
        super();
        this.inputs = Array.from(this.querySelectorAll('[data-dimension]'));
        this.unitSelect = this.querySelector('[data-unit-select]');
        this.typeButtons = Array.from(this.querySelectorAll('[data-variant-id]'));
        this.typeDescriptionOutput = this.querySelector('[data-foam-type-description]');
        this.priceOutput = this.querySelector('[data-foam-price]');
        this.variantIdField = this.querySelector('[data-foam-variant-id]');
        this.quantityField = this.querySelector('[data-foam-quantity]');
        this.propertyFields = {
          length: this.querySelector('[data-foam-property="length"]'),
          width: this.querySelector('[data-foam-property="width"]'),
          thickness: this.querySelector('[data-foam-property="thickness"]'),
          unit: this.querySelector('[data-foam-property="unit"]'),
          volume: this.querySelector('[data-foam-property="volume"]'),
          unitsPerPiece: this.querySelector('[data-foam-property="unitsPerPiece"]'),
          pieces: this.querySelector('[data-foam-property="pieces"]'),
        };

        this.unitVolume = parseFloat(this.dataset.unitVolume) || 2360;
        this.currentUnit = this.unitSelect ? this.unitSelect.value : 'cm';

        let variants = [];
        try {
          variants = JSON.parse(this.dataset.variants || '[]');
        } catch (error) {
          variants = [];
        }
        this.variants = variants;
        this.selectedVariant = variants.find((variant) => variant.available) || variants[0] || null;

        try {
          this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
            style: 'currency',
            currency: this.dataset.currency || 'GBP',
          });
        } catch (error) {
          this.formatter = null;
        }

        this.recalculate = this.recalculate.bind(this);
        this.handleUnitChange = this.handleUnitChange.bind(this);

        this.inputs.forEach((input) => input.addEventListener('input', this.recalculate));
        if (this.unitSelect) this.unitSelect.addEventListener('change', this.handleUnitChange);
        this.typeButtons.forEach((button) => {
          button.addEventListener('click', () => this.selectVariant(button));
        });
      }

      connectedCallback() {
        this.applyUnitBounds();
        this.recalculate();

        const initialButton = this.typeButtons.find((btn) => btn.classList.contains('is-selected'));
        if (initialButton) this.updateTypeDescription(initialButton);
      }

      selectVariant(button) {
        if (button.disabled) return;
        this.typeButtons.forEach((btn) => btn.classList.remove('is-selected'));
        button.classList.add('is-selected');
        this.selectedVariant = {
          id: parseFloat(button.dataset.variantId),
          price: parseFloat(button.dataset.variantPrice),
        };
        this.updateTypeDescription(button);
        this.recalculate();
      }

      updateTypeDescription(button) {
        if (this.typeDescriptionOutput) this.typeDescriptionOutput.textContent = button.dataset.variantDescription || '';
      }

      handleUnitChange() {
        const previousUnit = this.currentUnit;
        const nextUnit = this.unitSelect.value;
        const previousFactor = FOAM_CALC_UNIT_TO_CM[previousUnit] || 1;
        const nextFactor = FOAM_CALC_UNIT_TO_CM[nextUnit] || 1;

        this.inputs.forEach((input) => {
          const valueInCm = (parseFloat(input.value) || 0) * previousFactor;
          input.value = Math.round((valueInCm / nextFactor) * 100) / 100;
        });

        this.currentUnit = nextUnit;
        this.applyUnitBounds();
        this.recalculate();
      }

      applyUnitBounds() {
        const factor = FOAM_CALC_UNIT_TO_CM[this.currentUnit] || 1;
        this.inputs.forEach((input) => {
          const minCm = parseFloat(input.dataset.minCm);
          const maxCm = parseFloat(input.dataset.maxCm);
          if (!Number.isNaN(minCm)) input.min = Math.round((minCm / factor) * 100) / 100;
          if (!Number.isNaN(maxCm)) input.max = Math.round((maxCm / factor) * 100) / 100;
        });
      }

      recalculate() {
        const factor = FOAM_CALC_UNIT_TO_CM[this.currentUnit] || 1;
        const dimensionsCm = this.inputs.reduce((acc, input) => {
          const rawValue = Math.max(parseFloat(input.value) || 0, 0);
          acc[input.dataset.dimension] = rawValue * factor;
          return acc;
        }, {});

        const volumeCm3 = (dimensionsCm.length || 0) * (dimensionsCm.width || 0) * (dimensionsCm.thickness || 0);
        const volumeLitres = volumeCm3 / 1000;
        const volumeUnits = volumeCm3 > 0 ? Math.max(1, Math.round(volumeCm3 / this.unitVolume)) : 0;
        const unitPrice = this.selectedVariant ? this.selectedVariant.price : 0;
        const minUnits = unitPrice > 0 ? Math.ceil(FOAM_CALC_MIN_ORDER_PENCE / unitPrice) : 1;
        const units = volumeUnits > 0 ? Math.max(volumeUnits, minUnits) : 0;
        const estimatedPriceCents = units * unitPrice;

        if (this.priceOutput) {
          this.priceOutput.textContent = this.formatter
            ? this.formatter.format(estimatedPriceCents / 100)
            : (estimatedPriceCents / 100).toFixed(2);
        }

        if (this.variantIdField && this.selectedVariant) this.variantIdField.value = this.selectedVariant.id;
        if (this.quantityField) this.quantityField.value = units;
        if (this.propertyFields.length) this.propertyFields.length.value = dimensionsCm.length ? this.inputs.find((i) => i.dataset.dimension === 'length').value : '';
        if (this.propertyFields.width) this.propertyFields.width.value = this.inputs.find((i) => i.dataset.dimension === 'width').value;
        if (this.propertyFields.thickness) this.propertyFields.thickness.value = this.inputs.find((i) => i.dataset.dimension === 'thickness').value;
        if (this.propertyFields.unit) this.propertyFields.unit.value = this.currentUnit;
        if (this.propertyFields.volume) this.propertyFields.volume.value = `${volumeLitres.toFixed(1)} L`;
        if (this.propertyFields.unitsPerPiece) this.propertyFields.unitsPerPiece.value = units;
        // This form has no shopper-facing quantity box -- it adds exactly one
        // piece per submit -- so the count is 1 whenever there is a valid size.
        if (this.propertyFields.pieces) this.propertyFields.pieces.value = units > 0 ? 1 : '';
      }
    }
  );
}
